import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { ConfigService } from './config.service';

const SESSION_STORAGE_KEYS = ['token', 'login', 'vendorId'];

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private authenticatedSubject = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly router: Router,
    private readonly configService: ConfigService,
  ) {
    this.authenticatedSubject.next(this.readAuthenticatedFromStorage());
    this.watchStorageForSessionChanges();
  }

  isAuthenticated(): boolean {
    const authenticated = this.readAuthenticatedFromStorage();

    if (this.authenticatedSubject.value !== authenticated) {
      this.authenticatedSubject.next(authenticated);
    }

    return authenticated;
  }

  isAuthenticated$(): Observable<boolean> {
    return this.authenticatedSubject
      .asObservable()
      .pipe(distinctUntilChanged());
  }

  invoiceSubmissionRequiresLogin(): boolean {
    const config = this.resolveConfig();
    const requiresLoginValue = config?.invoiceSubmissionRequiresLogin;

    if (requiresLoginValue === null || requiresLoginValue === undefined) {
      return false;
    }

    return requiresLoginValue.toString().toLowerCase() === 'true';
  }

  canSubmitInvoice(): boolean {
    if (!this.configService.isResolved()) {
      return false;
    }

    return !this.invoiceSubmissionRequiresLogin() || this.isAuthenticated();
  }

  canSubmitInvoice$(): Observable<boolean> {
    return combineLatest([
      this.configService.getConfig$(),
      this.configService.isResolved$(),
      this.isAuthenticated$(),
    ]).pipe(
      map(() => this.canSubmitInvoice()),
      distinctUntilChanged(),
    );
  }

  logout(): void {
    this.clearSessionKeepingPortalConfig();
    this.redirectToLogin(undefined, true);
  }

  redirectToLogin(returnUrl?: string, reload: boolean = false): void {
    const navigation = returnUrl
      ? this.router.navigate(['login'], {
          queryParams: { redirect: 'invoice', returnUrl },
        })
      : this.router.navigateByUrl('login');

    navigation.then(() => {
      if (reload) {
        this.reloadPage();
      }
    });
  }

  reloadPage(): void {
    window.location.reload();
  }

  private clearSessionKeepingPortalConfig(): void {
    const portalConfig = localStorage.getItem('portalConfig');

    localStorage.clear();
    localStorage.setItem('login', JSON.stringify(false));

    if (portalConfig) {
      localStorage.setItem('portalConfig', portalConfig);
    }

    this.authenticatedSubject.next(false);
  }

  private watchStorageForSessionChanges(): void {
    if (typeof window === 'undefined' || !window.addEventListener) {
      return;
    }

    window.addEventListener('storage', (event: StorageEvent) => {
      if (
        event.key !== null &&
        SESSION_STORAGE_KEYS.indexOf(event.key) === -1
      ) {
        return;
      }

      this.authenticatedSubject.next(this.readAuthenticatedFromStorage());
    });
  }

  private readAuthenticatedFromStorage(): boolean {
    return (
      this.toBoolean(localStorage.getItem('login')) ||
      this.hasUsableToken(localStorage.getItem('token'))
    );
  }

  private hasUsableToken(rawToken: string | null): boolean {
    if (!rawToken) {
      return false;
    }

    let token = rawToken;
    try {
      const parsed = JSON.parse(rawToken);
      if (typeof parsed === 'string') {
        token = parsed;
      } else if (parsed === null) {
        return false;
      }
    } catch {
      token = rawToken;
    }

    return token.trim().length > 0 && token !== 'null' && token !== 'undefined';
  }

  private resolveConfig(): any {
    const currentConfig = this.configService.getConfig();
    if (currentConfig && Object.keys(currentConfig).length > 0) {
      return currentConfig;
    }

    return this.configService.getConfigFromStorage() ?? {};
  }

  private toBoolean(value: string | null): boolean {
    if (!value) {
      return false;
    }

    try {
      return JSON.parse(value) === true;
    } catch {
      return value.toLowerCase() === 'true';
    }
  }
}
